import { Link, useNavigate } from 'react-router-dom'
import { PageHeader, PageLayout } from '@/widgets/layout/ui/AppLayout'
import { SetupMap } from '@/widgets/setup-map'
import { StatsPanel } from '@/widgets/stats'
import { SkeletonStack } from '@/shared/ui'
import { useAccount } from '@/entities/session'
import { usePromptPreview } from '@/entities/profile'
import { CallRow, useCalls } from '@/entities/call'
import { ClientEventsWidget } from '@/entities/client-event/ui'
import { MinutesCard } from '@/entities/minutes'
import styles from './ui/DashboardPage.module.css'

/**
 * Обзор.
 *
 * Порядок блоков повторяет то, как кабинетом пользуются: сначала
 * позвонить и услышать, потом посмотреть цифры, потом разобрать
 * последние разговоры. Настройки ушли в свой раздел — сюда возвращаются
 * уже настроенными.
 */
export function DashboardPage() {
  const navigate = useNavigate()
  const { data: account } = useAccount()
  const preview = usePromptPreview()
  const calls = useCalls({})

  const recent = (calls.data ?? []).slice(0, 5)

  return (
    <PageLayout aside={<SetupMap />}>
      <PageHeader title={`Здравствуйте, ${account?.user.name || 'коллега'}`} />

      <div className={styles.hero}>
        <span className={styles.heroIcon}>
          <PhoneIcon />
        </span>
        <div className={styles.heroText}>
          <div className={styles.heroTitle}>Позвоните агенту и послушайте</div>
          <div className={styles.heroHint}>
            Он ведёт разговор по вашей анкете. Можно возражать, отказывать и перебивать —
            после звонка появится разбор.
          </div>
        </div>
        <div className={styles.heroAction}>
          <button type="button" className={styles.heroButton} onClick={() => navigate('/test-call')}>
            Позвонить
          </button>
        </div>
      </div>

      <MinutesCard />

      <ClientEventsWidget />

      <StatsPanel />

      <div className={styles.columns}>
        <div className={styles.panel}>
          <div className={styles.panelHead}>
            <span className={styles.panelTitle}>Последние разговоры</span>
            <Link className={styles.panelLink} to="/calls">
              все звонки →
            </Link>
          </div>

          {calls.isPending ? (
            <div style={{ padding: 'var(--space-4)' }}>
              <SkeletonStack rows={3} height={36} />
            </div>
          ) : recent.length ? (
            // Тот же компонент, что в полном списке: порядок колонок и
            // знаки должны совпадать, иначе переход в раздел требует
            // перечитывать строку заново. Сжатый вид — колонка уже.
            recent.map((call) => <CallRow key={call.id} call={call} compact showDate />)
          ) : (
            <p className={styles.empty}>Звонков ещё не было</p>
          )}
        </div>

        <div className={styles.panel}>
          <div className={styles.panelHead}>
            <span className={styles.panelTitle}>Первая фраза агента</span>
            <Link className={styles.panelLink} to="/settings/prompt">
              весь промпт →
            </Link>
          </div>

          {preview.isPending ? (
            <div style={{ padding: 'var(--space-4)' }}>
              <SkeletonStack rows={1} height={40} />
            </div>
          ) : (
            <p className={styles.greeting}>{preview.data?.greeting ?? '—'}</p>
          )}
        </div>
      </div>
    </PageLayout>
  )
}


function PhoneIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path
        d="M6.4 2.4L7.8 5.7L6.3 7.2C7.1 8.9 9.1 10.9 10.8 11.7L12.3 10.2L15.6 11.6V14.5C15.6 15.1 15.1 15.6 14.5 15.6C7.7 15.2 2.8 10.3 2.4 3.5C2.4 2.9 2.9 2.4 3.5 2.4H6.4Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  )
}
